import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, PencilRuler, Hammer, KeyRound } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { useEnquiry } from "../context/EnquiryContext";
import { ILLO } from "../lib/assets";

const STEPS = [
  {
    no: "01", phase: "Design", icon: PencilRuler, illo: ILLO.design,
    lead: "Understand, draw, visualise.",
    points: ["Free consultation & site understanding", "Space planning and 3D design", "Material, finish & budget alignment", "Final drawings you sign off on"],
  },
  {
    no: "02", phase: "Install", icon: Hammer, illo: ILLO.install,
    lead: "Build it to the exact spec.",
    points: ["Factory-made modular units", "Civil, flooring, ceiling & painting", "In-house project supervision", "Weekly progress transparency"],
  },
  {
    no: "03", phase: "Deliver", icon: KeyRound, illo: ILLO.deliver,
    lead: "A move-in-ready home.",
    points: ["Quality checks & snag list", "Deep clean & styling", "Handover with warranty", "Post-handover support"],
  },
];

export default function HowItWorks() {
  const { openEnquiry } = useEnquiry();
  return (
    <main className="bg-warm" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24 border-b border-navy/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Overline>How it works</Overline></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-5xl md:text-8xl font-light tracking-tighter text-navy mt-6 leading-[0.95]">
              Design. Install.<br /><span className="text-terracotta">Deliver.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-ink/75 text-lg leading-relaxed mt-8 max-w-xl">
              Three phases, one team. Here's exactly how DIDIT takes your home from a first conversation to the moment we hand over the keys.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 space-y-24 md:space-y-32">
          {STEPS.map((s, i) => (
            <div key={s.no} className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`} data-testid={`hiw-step-${s.no}`}>
              <div className="md:[direction:ltr]">
                <Reveal>
                  <div className="flex items-center gap-4">
                    <span className="font-display text-terracotta text-lg tracking-widest">{s.no}</span>
                    <s.icon className="text-navy/70" size={22} />
                  </div>
                </Reveal>
                <Reveal delay={0.05}>
                  <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight text-navy mt-5">{s.phase}</h2>
                </Reveal>
                <Reveal delay={0.08}>
                  <p className="font-display text-xl md:text-2xl text-navy/60 font-light mt-2">{s.lead}</p>
                </Reveal>
                <Reveal delay={0.12}>
                  <ul className="mt-8 space-y-4">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-ink/80">
                        <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2.5 shrink-0" />
                        <span className="leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
              <Reveal delay={0.1} className="md:[direction:ltr]">
                <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="relative aspect-square bg-white border border-navy/10">
                  <div className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-terracotta" />
                  <div className="absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2 border-terracotta" />
                  <img src={s.illo} alt={s.phase} className="w-full h-full object-contain p-8 mix-blend-multiply" />
                </motion.div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="bg-navy text-warm p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight max-w-lg leading-tight">
              Ready to start your DESIGN → INSTALL → DELIVER journey?
            </h2>
            <button onClick={() => openEnquiry("consultation", "how-it-works")} data-testid="hiw-consultation-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-terracotta text-warm px-8 py-4 text-sm font-medium hover:bg-warm hover:text-navy transition-colors shrink-0">
              Get Free Consultation
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
